import { AxiosResponse } from 'axios';
import { ApiResponse, User } from '../types';
import { API_BASE_URL } from '../constants/api';
import axios from 'axios';
import apiClient from './apiClient';

interface LoginResponse {
  user: User;
  token: string; // Legacy token format
  accessToken?: string; // New token format
  refreshToken?: string; // New token format
}

interface CreateGuestResponse {
  user: User;
  token: string;
}

interface EntraLoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
}

export const authService = {
  // Store user and tokens in local storage
  storeUserSession(user: User, accessToken: string, refreshToken: string): void {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  },
  async login(email: string, password: string): Promise<ApiResponse<LoginResponse>> {
    try {
      const response: AxiosResponse<ApiResponse<LoginResponse>> = await apiClient.post('/api/auth/login', {
        email,
        password,
      });
      
      // If login is successful, store the user session
      if (response.data.success && response.data.data.token) {
        // For backward compatibility, handle both token formats
        if (response.data.data.accessToken && response.data.data.refreshToken) {
          this.storeUserSession(
            response.data.data.user,
            response.data.data.accessToken,
            response.data.data.refreshToken
          );
        } else {
          // Legacy format with single token
          this.storeUserSession(
            response.data.data.user,
            response.data.data.token,
            '' // No refresh token in legacy format
          );
        }
      }
      
      return response.data;
    } catch (error: any) {
      return {
        data: {} as LoginResponse,
        success: false,
        message: error.response?.data?.message || 'Login failed',
        errors: error.response?.data?.errors,
      };
    }
  },

  async createGuestUser(displayName: string): Promise<ApiResponse<CreateGuestResponse>> {
    try {
      const response: AxiosResponse<ApiResponse<CreateGuestResponse>> = await apiClient.post('/api/auth/guest', {
        displayName,
      });
      return response.data;
    } catch (error: any) {
      return {
        data: {} as CreateGuestResponse,
        success: false,
        message: error.response?.data?.message || 'Failed to create guest user',
        errors: error.response?.data?.errors,
      };
    }
  },

  async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      const response: AxiosResponse<ApiResponse<User>> = await apiClient.get('/api/auth/me');
      return response.data;
    } catch (error: any) {
      return {
        data: {} as User,
        success: false,
        message: error.response?.data?.message || 'Failed to get current user',
        errors: error.response?.data?.errors,
      };
    }
  },

  async refreshToken(): Promise<ApiResponse<{ accessToken: string, refreshToken: string }>> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response: AxiosResponse<ApiResponse<{ accessToken: string, refreshToken: string }>> = 
        await apiClient.post('/api/auth/refresh', { refreshToken });
      
      if (response.data.success) {
        // Update stored tokens
        localStorage.setItem('token', response.data.data.accessToken);
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
      }
      
      return response.data;
    } catch (error: any) {
      // On refresh failure, clear tokens and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      return {
        data: { accessToken: '', refreshToken: '' },
        success: false,
        message: error.response?.data?.message || 'Failed to refresh token',
        errors: error.response?.data?.errors,
      };
    }
  },

  async loginWithEntra(accessToken: string, idToken?: string): Promise<EntraLoginResponse> {
    try {
      // Debug logging - token format and length
      console.log('Token format check:', {
        accessToken: {
          length: accessToken.length,
          firstChars: accessToken.substring(0, 20) + '...',
          lastChars: '...' + accessToken.substring(accessToken.length - 20),
          dotCount: accessToken.split('.').length - 1
        },
        idToken: idToken ? {
          length: idToken.length,
          firstChars: idToken.substring(0, 20) + '...',
          lastChars: '...' + idToken.substring(idToken.length - 20),
          dotCount: idToken.split('.').length - 1
        } : 'not provided'
      });
      
      // API_BASE_URL already includes /api, so we don't need to include it in the path
      console.log('Making API request to:', API_BASE_URL + '/api/auth/entra');
      const response: AxiosResponse<EntraLoginResponse> = await apiClient.post('/api/auth/entra', {
        accessToken,
        idToken
      });
      
      console.log('Entra login success:', response.status);
      return response.data;
    } catch (error: any) {
      console.error('Entra login error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      // Return an empty EntraLoginResponse object
      return {
        user: {} as User,
        accessToken: '',
        refreshToken: '',
        tokenType: '',
        expiresIn: 0
      };
    }
  },

  async logout(): Promise<void> {
    try {
      // Use axios directly to bypass token refresh interceptor
      // This ensures logout works even with expired tokens
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
      await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, {
        // Include the token if available but don't fail if it's not
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        }
      }).catch((err: Error) => {
        // Log but continue with logout regardless of server response
        console.log('Logout API call failed, proceeding with local cleanup:', err);
      });
    } finally {
      // Always clean up local storage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  },

  /**
   * Converts a guest user to a registered user
   * This will create a host record for the user as well
   */
  async convertGuestToRegistered(email: string, password: string, displayName?: string): Promise<ApiResponse<LoginResponse>> {
    try {
      // Get current user info to verify it's a guest
      const currentUser = localStorage.getItem('user');
      if (!currentUser) {
        throw new Error('No user is currently logged in');
      }
      
      const user = JSON.parse(currentUser) as User;
      if (!user.isGuest) {
        throw new Error('Current user is not a guest');
      }
      
      // Call the API to convert the guest user
      const response: AxiosResponse<ApiResponse<LoginResponse>> = await apiClient.post('/api/auth/convert-guest', {
        email,
        password,
        displayName: displayName || user.displayName
      });
      
      // If conversion is successful, update the user session
      if (response.data.success) {
        if (response.data.data.accessToken && response.data.data.refreshToken) {
          this.storeUserSession(
            response.data.data.user,
            response.data.data.accessToken,
            response.data.data.refreshToken
          );
        } else {
          // Legacy format with single token
          this.storeUserSession(
            response.data.data.user,
            response.data.data.token,
            '' // No refresh token in legacy format
          );
        }
      }
      
      return response.data;
    } catch (error: any) {
      return {
        data: {} as LoginResponse,
        success: false,
        message: error.response?.data?.message || 'Failed to convert guest user',
        errors: error.response?.data?.errors,
      };
    }
  },
};

export default authService;
